import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, X, Save, Upload, Box, Tag, Eye, Bold, Italic, Strikethrough, List, ListOrdered, Heading2, Heading3, Link2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TiptapLink from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { api, upload3dFile, uploadFile, BASE } from "../../services/api";
import ModelViewer from "../../components/stage/ModelViewer";

const EMPTY = { title: "", description: "", body: "", file_url: "", thumbnail_url: "", images: [], tags: [], order: 99 };

function resolveUrl(url) {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

// ── Rich text editor ──────────────────────────────────────────────────────────
function TBtn({ active, onClick, title, children }) {
    return (
        <button
            type="button"
            onMouseDown={e => { e.preventDefault(); onClick(); }}
            title={title}
            className={`p-1.5 rounded transition-colors text-sm font-medium leading-none ${
                active
                    ? "bg-nova-cyan/20 text-nova-cyan"
                    : "text-zinc-400 hover:text-white hover:bg-white/8"
            }`}
        >
            {children}
        </button>
    );
}

function RichTextEditor({ value, onChange, placeholder = "Napíšte detailný popis…" }) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ link: false }),
            TiptapLink.configure({ openOnClick: false }),
            Placeholder.configure({ placeholder }),
        ],
        content: value || "",
        onUpdate: ({ editor }) => onChange(editor.getHTML()),
        editorProps: {
            attributes: {
                class: "outline-none min-h-[220px] text-zinc-300 text-sm leading-relaxed px-4 py-3",
            },
        },
    });

    const setLink = useCallback(() => {
        const prev = editor?.getAttributes("link").href || "";
        const url = window.prompt("URL odkazu", prev);
        if (url === null) return;
        if (url === "") { editor?.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
        editor?.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }, [editor]);

    if (!editor) return null;

    return (
        <div className="rounded-xl border border-white/8 focus-within:border-nova-cyan/30 overflow-hidden bg-nova-black/40">
            {/* Toolbar */}
            <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-white/8 flex-wrap bg-nova-surface/30">
                <TBtn active={editor.isActive("bold")}          onClick={() => editor.chain().focus().toggleBold().run()}                title="Tučné"><Bold className="w-3.5 h-3.5" /></TBtn>
                <TBtn active={editor.isActive("italic")}        onClick={() => editor.chain().focus().toggleItalic().run()}              title="Kurzíva"><Italic className="w-3.5 h-3.5" /></TBtn>
                <TBtn active={editor.isActive("strike")}        onClick={() => editor.chain().focus().toggleStrike().run()}              title="Prečiarknuté"><Strikethrough className="w-3.5 h-3.5" /></TBtn>
                <div className="w-px h-4 bg-white/10 mx-1" />
                <TBtn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Nadpis 2"><Heading2 className="w-3.5 h-3.5" /></TBtn>
                <TBtn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Nadpis 3"><Heading3 className="w-3.5 h-3.5" /></TBtn>
                <div className="w-px h-4 bg-white/10 mx-1" />
                <TBtn active={editor.isActive("bulletList")}    onClick={() => editor.chain().focus().toggleBulletList().run()}           title="Zoznam"><List className="w-3.5 h-3.5" /></TBtn>
                <TBtn active={editor.isActive("orderedList")}   onClick={() => editor.chain().focus().toggleOrderedList().run()}          title="Číslovaný zoznam"><ListOrdered className="w-3.5 h-3.5" /></TBtn>
                <div className="w-px h-4 bg-white/10 mx-1" />
                <TBtn active={editor.isActive("link")}          onClick={setLink}                                                         title="Odkaz"><Link2 className="w-3.5 h-3.5" /></TBtn>
            </div>
            {/* Content */}
            <EditorContent editor={editor} />
        </div>
    );
}

// ── 3D upload zone ────────────────────────────────────────────────────────────
function ModelUploadZone({ value, onChange }) {
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const handle = async (file) => {
        if (!file) return;
        const ext = file.name.split(".").pop().toLowerCase();
        if (!["glb", "gltf", "obj"].includes(ext)) {
            toast.error("Podporované formáty: .glb, .gltf, .obj");
            return;
        }
        setUploading(true);
        try {
            const result = await upload3dFile(file);
            onChange(result.url);
            toast.success(`Model nahraný (${result.size_mb} MB)`);
        } catch (e) {
            toast.error(e.message || "Upload zlyhal");
        } finally {
            setUploading(false);
        }
    };

    if (value) {
        return (
            <div className="space-y-2">
                <div className="flex items-center gap-3 p-3 rounded-xl border border-nova-cyan/30 bg-nova-cyan/5">
                    <Box className="w-4 h-4 text-nova-cyan shrink-0" />
                    <span className="font-mono text-xs text-zinc-300 truncate flex-1">{value.split("/").pop()}</span>
                    <button type="button" onClick={() => onChange("")} className="text-zinc-500 hover:text-red-400 transition-colors">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <button type="button" onClick={() => inputRef.current?.click()}
                    className="text-xs text-zinc-500 hover:text-nova-cyan transition-colors font-mono">
                    Vymeniť súbor
                </button>
                <input ref={inputRef} type="file" accept=".glb,.gltf,.obj" className="hidden"
                    onChange={e => handle(e.target.files?.[0])} />
            </div>
        );
    }

    return (
        <div
            onClick={() => !uploading && inputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); handle(e.dataTransfer.files[0]); }}
            className={`flex flex-col items-center gap-3 p-8 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                dragging ? "border-nova-cyan bg-nova-cyan/8" : "border-white/15 hover:border-white/30"
            }`}
        >
            <input ref={inputRef} type="file" accept=".glb,.gltf,.obj" className="hidden"
                onChange={e => handle(e.target.files?.[0])} />
            <Upload className={`w-7 h-7 ${dragging ? "text-nova-cyan" : "text-zinc-600"}`} />
            <div className="text-center">
                <div className="text-white text-sm font-medium">{uploading ? "Nahrávam…" : "Nahrať 3D model"}</div>
                <div className="text-zinc-500 text-xs mt-0.5">.glb · .gltf · .obj — Drag & drop alebo kliknite</div>
            </div>
        </div>
    );
}

// ── Multi-image upload ────────────────────────────────────────────────────────
function MultiImageUpload({ images, onChange }) {
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const upload = async (file) => {
        setUploading(true);
        try {
            const res = await uploadFile(file);
            onChange([...(images || []), res.url]);
            toast.success("Fotka nahraná");
        } catch (e) {
            toast.error(e.message || "Upload zlyhal");
        } finally {
            setUploading(false);
        }
    };

    const handleFiles = (files) => {
        Array.from(files || []).forEach(upload);
    };

    const remove = (idx) => onChange((images || []).filter((_, i) => i !== idx));

    return (
        <div className="space-y-3">
            {(images || []).length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                    {images.map((url, i) => (
                        <div key={i} className="relative group aspect-video rounded-lg overflow-hidden border border-white/10 bg-nova-black/50">
                            <img src={resolveUrl(url)} alt="" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-start justify-end p-1">
                                <button
                                    type="button"
                                    onClick={() => remove(i)}
                                    className="p-1 rounded bg-black/70 text-zinc-400 hover:text-red-400 transition-colors"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                            {i === 0 && (
                                <div className="absolute bottom-1 left-1 font-mono text-[8px] px-1.5 py-0.5 rounded bg-nova-cyan/20 text-nova-cyan border border-nova-cyan/30">
                                    Cover
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="w-full py-3 rounded-xl border border-dashed border-white/15 text-zinc-500 hover:border-white/30 hover:text-zinc-300 transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            >
                <ImageIcon className="w-4 h-4" />
                {uploading ? "Nahrávam…" : "Pridať fotky"}
            </button>
            <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
                onChange={e => handleFiles(e.target.files)} />
            {(images || []).length > 0 && (
                <div className="text-zinc-700 text-[10px] font-mono">Prvá fotka sa použije ako cover na karte</div>
            )}
        </div>
    );
}

// ── Tags editor ───────────────────────────────────────────────────────────────
function TagsEditor({ tags, onChange }) {
    const [input, setInput] = useState("");
    const add = () => {
        const v = input.trim();
        if (v && !tags.includes(v)) onChange([...tags, v]);
        setInput("");
    };
    return (
        <div>
            <div className="flex flex-wrap gap-1.5 mb-2">
                {tags.map(t => (
                    <span key={t} className="flex items-center gap-1 bg-nova-surface border border-white/10 rounded-full px-2.5 py-1 text-xs text-white">
                        {t}
                        <button type="button" onClick={() => onChange(tags.filter(x => x !== t))} className="text-zinc-500 hover:text-red-400 transition-colors">
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
            </div>
            <div className="flex gap-2">
                <input
                    value={input} onChange={e => setInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())}
                    placeholder="Pridať tag… (Enter)"
                    className="flex-1 bg-nova-black/50 border border-white/8 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-nova-cyan/40"
                />
                <button type="button" onClick={add} className="px-3 py-2 rounded-xl bg-nova-surface border border-white/8 text-zinc-400 hover:text-white transition-colors">
                    <Plus className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

// ── Edit / Create panel ───────────────────────────────────────────────────────
function EditPanel({ item, onClose, onSaved }) {
    const [form, setForm] = useState(item || EMPTY);
    const [saving, setSaving] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const isNew = !item;

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const save = async () => {
        if (!form.title.trim()) { toast.error("Vyplňte názov"); return; }
        if (!form.file_url)     { toast.error("Nahrajte 3D súbor"); return; }
        setSaving(true);
        try {
            const payload = { ...form, thumbnail_url: form.images?.[0] || form.thumbnail_url || "" };
            const saved = isNew
                ? await api.createModel3d(payload)
                : await api.updateModel3d(item.id, payload);
            toast.success(isNew ? "Vizualizácia vytvorená" : "Vizualizácia uložená");
            onSaved(saved);
        } catch (e) {
            toast.error(e.message || "Chyba");
        } finally {
            setSaving(false);
        }
    };

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-end">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <motion.div
                initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 28, stiffness: 280 }}
                className="relative w-full max-w-2xl h-screen bg-nova-surface border-l border-white/8 flex flex-col overflow-hidden"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/8 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-nova-cyan/10 border border-nova-cyan/20 flex items-center justify-center">
                            <Box className="w-4 h-4 text-nova-cyan" />
                        </div>
                        <div>
                            <div className="font-heading font-black text-white text-sm">{isNew ? "Nová vizualizácia" : form.title}</div>
                            <div className="font-mono text-[9px] text-zinc-500 uppercase tracking-wider mt-0.5">{isNew ? "Pridanie" : "Úprava"}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {form.file_url && (
                            <button type="button" onClick={() => setPreviewOpen(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-zinc-400 hover:text-white text-xs transition-colors">
                                <Eye className="w-3.5 h-3.5" /> Náhľad 3D
                            </button>
                        )}
                        <button type="button" onClick={save} disabled={saving}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nova-cyan text-nova-black text-sm font-semibold hover:bg-nova-cyan/90 disabled:opacity-50 transition-colors">
                            <Save className="w-3.5 h-3.5" />
                            {saving ? "Ukladám…" : "Uložiť"}
                        </button>
                        <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white p-1 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Form */}
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-7">

                    {/* Názov */}
                    <div>
                        <label className="field-label">Názov *</label>
                        <input value={form.title} onChange={e => set("title", e.target.value)}
                            placeholder="napr. Festivalová scéna — Pohoda 2024"
                            className="field-input font-heading font-bold" />
                    </div>

                    {/* Krátky popis */}
                    <div>
                        <label className="field-label">Krátky popis <span className="text-zinc-700 normal-case">(zobrazí sa pod názvom)</span></label>
                        <input value={form.description} onChange={e => set("description", e.target.value)}
                            placeholder="1–2 vety o objekte alebo evente"
                            className="field-input" />
                    </div>

                    {/* 3D súbor */}
                    <div>
                        <label className="field-label">3D súbor (.glb / .gltf / .obj) *</label>
                        <ModelUploadZone value={form.file_url} onChange={v => set("file_url", v)} />
                    </div>

                    {/* Fotky */}
                    <div>
                        <label className="field-label">
                            <ImageIcon className="w-3 h-3 inline mr-1" />
                            Fotky
                        </label>
                        <MultiImageUpload images={form.images || []} onChange={v => set("images", v)} />
                    </div>

                    {/* Rich text */}
                    <div>
                        <label className="field-label">Detailný popis</label>
                        <RichTextEditor
                            key={item?.id || "new"}
                            value={form.body}
                            onChange={v => set("body", v)}
                        />
                    </div>

                    {/* Tagy + Poradie */}
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="field-label"><Tag className="w-3 h-3 inline mr-1" />Tagy</label>
                            <TagsEditor tags={form.tags || []} onChange={v => set("tags", v)} />
                        </div>
                        <div>
                            <label className="field-label">Poradie</label>
                            <input type="number" value={form.order ?? 99} onChange={e => set("order", parseInt(e.target.value) || 99)}
                                className="w-24 bg-nova-black/50 border border-white/8 rounded-xl px-4 py-2.5 text-white outline-none focus:border-nova-cyan/40 font-mono" />
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* 3D Preview */}
            <AnimatePresence>
                {previewOpen && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] bg-nova-black flex flex-col">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-white/8">
                            <span className="font-heading font-black text-white">{form.title || "Náhľad"}</span>
                            <button type="button" onClick={() => setPreviewOpen(false)} className="text-zinc-400 hover:text-white p-1">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1">
                            <ModelViewer fileUrl={form.file_url} height="100%" />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminVizualizacie() {
    const [models, setModels]     = useState([]);
    const [loading, setLoading]   = useState(true);
    const [editing, setEditing]   = useState(null);
    const [creating, setCreating] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try { setModels(await api.getModels3d()); }
        catch { toast.error("Nepodarilo sa načítať vizualizácie"); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleSaved = (saved) => {
        setModels(prev => {
            const exists = prev.find(m => m.id === saved.id);
            return exists ? prev.map(m => m.id === saved.id ? saved : m) : [...prev, saved];
        });
        setEditing(null);
        setCreating(false);
    };

    const handleDelete = async (id, title) => {
        if (!window.confirm(`Zmazať „${title}"?`)) return;
        setDeleting(id);
        try {
            await api.deleteModel3d(id);
            setModels(prev => prev.filter(m => m.id !== id));
            toast.success("Vizualizácia zmazaná");
        } catch (e) { toast.error(e.message); }
        finally { setDeleting(null); }
    };

    return (
        <>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-heading text-2xl font-black text-white tracking-tight">3D Vizualizácie</h1>
                    <p className="text-zinc-500 text-sm mt-1">
                        Každá položka môže obsahovať 3D model, fotky aj detailný popis.
                    </p>
                </div>
                <button onClick={() => setCreating(true)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-nova-cyan text-nova-black font-semibold text-sm hover:bg-nova-cyan/90 transition-colors">
                    <Plus className="w-4 h-4" /> Pridať vizualizáciu
                </button>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
                    {[1,2,3].map(n => <div key={n} className="h-56 bg-nova-surface/40 rounded-2xl" />)}
                </div>
            ) : models.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-zinc-600">
                    <Box className="w-12 h-12 mb-4 opacity-20" />
                    <div className="text-sm font-medium mb-1">Žiadne vizualizácie</div>
                    <div className="text-xs text-zinc-700">Pridajte prvú vizualizáciu kliknutím na tlačidlo vyššie</div>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {models.map(m => {
                        const cover = m.images?.[0] || m.thumbnail_url || "";
                        const ext   = m.file_url?.split(".").pop()?.toUpperCase() || "3D";
                        return (
                            <motion.div key={m.id} whileHover={{ y: -2 }}
                                className="group relative rounded-2xl border border-white/8 hover:border-white/20 transition-all bg-nova-surface/40 overflow-hidden">
                                <div className="h-44 overflow-hidden relative bg-nova-black/60">
                                    {cover ? (
                                        <img src={resolveUrl(cover)} alt={m.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Box className="w-10 h-10 text-zinc-700" />
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-nova-black/80 to-transparent" />
                                    <div className="absolute top-2.5 left-2.5 font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded bg-nova-cyan/15 border border-nova-cyan/30 text-nova-cyan">
                                        {ext}
                                    </div>
                                    {(m.images?.length > 0) && (
                                        <div className="absolute top-2.5 right-2.5 font-mono text-[9px] px-2 py-0.5 rounded bg-black/50 text-zinc-400 border border-white/10">
                                            {m.images.length} foto
                                        </div>
                                    )}
                                </div>

                                <div className="p-4">
                                    <div className="font-heading font-bold text-white text-sm truncate mb-0.5">{m.title}</div>
                                    <div className="text-zinc-600 text-xs line-clamp-2">{m.description}</div>

                                    {(m.tags || []).length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2.5">
                                            {m.tags.slice(0, 3).map(t => (
                                                <span key={t} className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-500">{t}</span>
                                            ))}
                                        </div>
                                    )}

                                    <div className="flex gap-2 mt-3">
                                        <button onClick={() => setEditing(m)}
                                            className="flex-1 py-1.5 rounded-lg border border-white/8 text-zinc-400 hover:text-white hover:border-white/20 transition-colors text-xs font-medium">
                                            Upraviť
                                        </button>
                                        <button
                                            onClick={() => handleDelete(m.id, m.title)}
                                            disabled={deleting === m.id}
                                            className="px-3 py-1.5 rounded-lg border border-white/8 text-zinc-600 hover:text-red-400 hover:border-red-500/20 transition-colors disabled:opacity-40">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            )}

            <AnimatePresence>
                {(creating || editing) && (
                    <EditPanel
                        key={editing?.id || "new"}
                        item={editing}
                        onClose={() => { setEditing(null); setCreating(false); }}
                        onSaved={handleSaved}
                    />
                )}
            </AnimatePresence>
        </>
    );
}
